import React from 'react';
import { Instagram, ExternalLink } from 'lucide-react';
import { companyInfo } from '../data/mock';

const InstagramGallery = () => {
  // Mock Instagram posts
  const instagramPosts = [
    {
      id: 1,
      image: "https://images.unsplash.com/photo-1772567732969-c1506edf80a0",
      caption: "Modern kitchen transformation 🏠✨",
      likes: 245
    },
    {
      id: 2,
      image: "https://images.unsplash.com/photo-1696987007764-7f8b85dd3033",
      caption: "Luxury bathroom renovation complete! 🛁",
      likes: 189
    },
    {
      id: 3,
      image: "https://images.unsplash.com/flagged/photo-1573168710465-7f7da9a23a15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxob21lJTIwcmVub3ZhdGlvbnxlbnwwfHx8fDE3NzMzNjc5Mzd8MA&ixlib=rb-4.1.0&q=85",
      caption: "Beautiful renovation transformation ✨",
      likes: 312
    },
    {
      id: 4,
      image: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914",
      caption: "Another dream home completed! 🌟",
      likes: 428
    },
    {
      id: 5,
      image: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c",
      caption: "Contemporary kitchen design ⚡",
      likes: 276
    },
    {
      id: 6,
      image: "https://images.unsplash.com/photo-1763485955499-f11dcfdda823",
      caption: "Premium finishes that make a difference 💎",
      likes: 198
    },
    {
      id: 7,
      image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72",
      caption: "Commercial space transformation 🏢",
      likes: 234
    },
    {
      id: 8,
      image: "https://images.unsplash.com/photo-1718220216044-006f43e3a9b1",
      caption: "Modern office renovation 💼",
      likes: 167
    }
  ];

  // Pattern background with Massimo logos
  const PatternBackground = () => (
    <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden">
      <div className="grid grid-cols-4 gap-4 p-4" style={{ 
        transform: 'rotate(-15deg) scale(1.5)',
        transformOrigin: 'center'
      }}>
        {[...Array(32)].map((_, i) => (
          <div key={i} className="w-32 h-32">
            <img
              src="https://customer-assets.emergentagent.com/job_home-improvement-pro/artifacts/uxclx2q7_Massimo_logo%2010.png"
              alt=""
              className="w-full h-full object-contain opacity-30"
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#484237' }}>
      <PatternBackground />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <Instagram className="text-[#D7AC78] mr-3" size={56} />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-[#D7AC78] mb-6 leading-tight tracking-tight">
              GALLERY
            </h1>
            <p className="text-xl text-[#F8E6C6] leading-relaxed mb-8">
              Explore our latest projects and get inspired by our transformations
            </p>
            <a
              href={companyInfo.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-[#D7AC78] text-[#484237] font-bold rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              <Instagram className="mr-2" size={20} />
              @MassimoConstructionUSA
              <ExternalLink className="ml-2" size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Modern Gallery Grid */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {instagramPosts.map((post, index) => {
              // Create varied sizes for bento grid effect
              const isLarge = index === 0 || index === 3;
              const isTall = index === 1 || index === 5;
              
              return (
                <a
                  key={post.id}
                  href={companyInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative overflow-hidden rounded-2xl bg-[#728481] ${
                    isLarge ? 'md:col-span-2 md:row-span-2' : 
                    isTall ? 'md:row-span-2' : ''
                  }`}
                  style={{
                    aspectRatio: isLarge ? '1/1' : isTall ? '1/2' : '1/1',
                    border: '3px solid #D7AC78'
                  }}
                >
                  <img
                    src={post.image}
                    alt={post.caption}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  
                  {/* Overlay with logo pattern on hover */}
                  <div className="absolute inset-0 bg-[#484237]/95 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center p-6">
                    <img
                      src="https://customer-assets.emergentagent.com/job_home-improvement-pro/artifacts/uxclx2q7_Massimo_logo%2010.png"
                      alt="Massimo"
                      className="w-24 h-24 mb-4 opacity-40"
                    />
                    <p className="text-[#D7AC78] text-center mb-4 font-semibold">{post.caption}</p>
                    <div className="flex items-center space-x-2 text-[#D7AC78]">
                      <svg className="w-6 h-6 fill-current" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                      <span className="font-bold text-xl">{post.likes}</span>
                    </div>
                  </div>

                  {/* Corner accent */}
                  <div className="absolute top-3 right-3 w-10 h-10 bg-[#D7AC78] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                    <Instagram size={20} className="text-[#484237]" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Decorative Section with Pattern */}
      <section className="py-16 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-[#D7AC78] rounded-3xl p-12 shadow-2xl border-4 border-[#728481]">
            <h2 className="text-4xl md:text-5xl font-bold text-[#484237] mb-6">
              Want to See More?
            </h2>
            <p className="text-xl text-[#484237] mb-8 opacity-90">
              Follow us on Instagram for daily updates, behind-the-scenes content, and renovation inspiration
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={companyInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#484237] text-[#D7AC78] font-bold rounded-lg hover:bg-[#3a3530] transform hover:scale-105 transition-all duration-300 shadow-xl"
              >
                <Instagram className="mr-2" size={20} />
                Follow Us
              </a>
              <a
                href="/contato"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#484237] font-bold rounded-lg hover:bg-gray-100 border-3 border-[#484237] transform hover:scale-105 transition-all duration-300"
              >
                Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Pattern */}
      <div className="h-32 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-10">
          <img
            src="https://customer-assets.emergentagent.com/job_home-improvement-pro/artifacts/uxclx2q7_Massimo_logo%2010.png"
            alt=""
            className="h-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default InstagramGallery;
